/**
 * preflightToken (Inventory Screens Step 4.3b) — SILENT-ONLY token acquisition
 * for the field-zone drain. The D-4.K seam.
 *
 * getApiToken() (auth/msalInstance.js) fires acquireTokenRedirect on an
 * interaction-required failure — a redirect mid-drain would navigate the user
 * away with items half-replayed. The drain therefore preflights through THIS
 * helper instead: token on success, null on ANY failure. It NEVER throws,
 * never calls getApiToken, and NEVER redirects.
 *
 * CONTRACT: SILENT ONLY. THE CALLER HALTS; THIS MODULE NEVER REDIRECTS.
 * Every arm that is not a live token returns null and does nothing else — no
 * interactive escalation, no latch, no navigation. The one-per-session
 * acquireTokenRedirect this module used to own (gate 5A-2's "HALT AND PROMPT",
 * latched in sessionStorage under 'ts_interactive_attempted') is REMOVED. It
 * was reachable from exactly one call site — drainFieldQueue.js — and only for
 * a rep who happened to be holding a non-empty queue, because the drain
 * returns before the preflight when nothing is pending. An escalation that
 * cannot fire for most of the users who need it is not a recovery path, and
 * the latch that throttled it was invisible to getApiToken, which still
 * redirects unlatched on the app-wide path. Re-auth belongs to the surfaces
 * that own that state — FieldReauthPrompt for a dead session, the App-level
 * authError panel for a rejected token — not to a background drain.
 *
 * Null is a HALT, not a degradation. d06c0eb made drainFieldQueue stop the
 * whole cycle on a null token instead of proceeding Bearer-less: items stay
 * 'pending' with retry_count untouched, and the next cycle replays them once a
 * token is available. Post-gate-3.6 the backend rejects a tokenless write, so
 * a Bearer-less send could only burn the retry ladder toward the terminal
 * 'failed' state. That halt is the whole of the contract on this side.
 */

import { msalInstance } from '../../../auth/msalInstance';

/** @returns {Promise<string|null>} access token, or null on ANY failure. */
export async function getFieldTokenSilent() {
  const scope = process.env.REACT_APP_API_SCOPE;
  try {
    const account = msalInstance.getAllAccounts()[0];
    // No session at all is not an acquisition failure — FieldReauthPrompt owns
    // that state. Acting on it here would fight it.
    if (!account) return null;
    if (!scope) return null; // misconfiguration, not an auth failure
    const res = await msalInstance.acquireTokenSilent({ scopes: [scope], account });
    const token = res.accessToken || null;
    if (token) return token;
    // Silent call resolved but carried no token — same halt as a throw.
    console.warn('preflightToken: silent acquisition returned no access token');
    return null;
  } catch (e) {
    console.warn('preflightToken: silent acquisition failed', e?.errorCode || e?.message);
    return null;
  }
}
